import { UiEventType } from '@enums/game-events.enum';
import { SceneKey } from '@models/scene-keys';
import EventEngine from '@services/core/EventEngine';
import { GameConfigService } from '@services/core/GameConfigService';
import GameState from '@services/core/GameState';
import { ActionRegistry } from '@services/core/ActionRegistry';
import { BoardService } from '@services/game/BoardService';
import { MovementService } from '@services/game/MovementService';
import { LayoutService } from '@services/ui/LayoutService';
import { preloadBubbles } from '../ui/animations/BubblesFx';
import { preloadCelebration } from '../ui/animations/CelebrationFx';
import { createBoard } from '../ui/Board';
import { GameBackground } from './GameBackground';

export default class Game extends Phaser.Scene {
  private layout!: LayoutService;
  private configService!: GameConfigService;
  private gameState!: GameState;
  private eventEngine!: EventEngine;
  private movementService!: MovementService;
  private board!: ReturnType<typeof createBoard>;
  private gameBg!: GameBackground;

  constructor() {
    super(SceneKey.Game);
  }

  preload() {
    preloadCelebration(this);
    preloadBubbles(this);
  }

  create() {
    // Initialize core services
    const boardService = new BoardService();
    this.configService = new GameConfigService();
    this.layout = new LayoutService(this.scale);
    this.gameState = new GameState();
    this.gameBg = new GameBackground(this, this.layout);
    this.gameBg.create();

    // Initialize game for the current stage
    const stage = this.configService.currentStage!;
    this.gameState.resetStage(
      stage,
      this.configService.gameStartPosition,
      this.configService.gameGoals,
    );
    const boardConfig = this.configService.boardConfig!;

    // Create a board view
    this.board = createBoard(this, boardConfig, boardService, {
      startPosition: this.gameState.getPosition(),
    });
    // create decorations around board
    this.gameBg.setDecorations(this.board.layout.worldHeight);
    // Initialize game services
    this.movementService = new MovementService(
      this,
      this.gameState,
      this.board,
    );

    // Initialize EventEngine and inject a shared state
    this.eventEngine = new EventEngine();

    // Register all action handlers via ActionRegistry
    const actionRegistry = new ActionRegistry(
      this.eventEngine.getActionMap(),
      this.gameState,
      this.configService,
      this.movementService,
    );
    actionRegistry.registerAll();

    // Launch UI scene
    this.launchUIScene();

    // Listen for finish event to show the win screen overlay
    this.events.on(UiEventType.FINISH_GAME, this.onFinishGame, this);

    // drag-to-scroll the camera
    this.setupDevDragScroll();
  }

  private launchUIScene(): void {
    // Launch the UI Scene in parallel
    this.scene.launch(SceneKey.UI);

    // Ensure UI is on top (optional, usually launch order handles this)
    this.scene.bringToTop(SceneKey.UI);
  }

  private setupDevDragScroll(): void {
    let lastY = 0;
    let dragging = false;

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragging = true;
      lastY = p.y;
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging || !p.isDown) return;
      // Keep lastY in sync even when a decor image is being dragged so the
      // camera doesn't jump when the decor drag ends.
      if (this.data.get('decorDragging')) {
        lastY = p.y;
        return;
      }
      const dy = lastY - p.y;
      lastY = p.y;
      this.cameras.main.scrollY += dy;
    });

    this.input.on('pointerup', () => {
      dragging = false;
    });

    this.input.on(
      'wheel',
      (
        _pointer: Phaser.Input.Pointer,
        _currentlyOver: Phaser.GameObjects.GameObject[],
        _deltaX: number,
        deltaY: number,
      ) => {
        this.cameras.main.scrollY += deltaY;
      },
    );
  }

  private onFinishGame(): void {
    this.scene.stop(SceneKey.UI);
    this.scene.launch(SceneKey.Finish);
    this.scene.bringToTop(SceneKey.Finish);
  }

  shutdown() {
    this.events.off(UiEventType.FINISH_GAME, this.onFinishGame, this);
    // Clean up background resize listener
    if (this.gameBg) {
      this.gameBg.destroy();
    }

    // Clean up board resources (removes resize listener)
    if (this.board) {
      this.board.destroy();
    }

    // Clear action map to prevent memory leaks
    if (this.eventEngine) {
      this.eventEngine.clear();
    }
  }
}
